/**
 * Opening an outside page — a Zoho or Meet meeting — from a button the person pressed.
 *
 * On the web that is a new tab. It has to be opened INSIDE the click handler, before anything is
 * awaited: a window opened later is no longer "a user gesture" and the browser blocks it as a popup.
 * If it is blocked anyway (`window.open` gives back `null`), the page itself navigates instead, so
 * the person still gets into the meeting.
 *
 * In the phone app (Capacitor) it is the in-app browser (`@capacitor/browser`: Chrome Custom Tabs on
 * Android). The person stays inside PeopleOrbit's window, and its own Close button comes back to the
 * page they were on. A plain `window.open` there would hand the link to the phone's default browser
 * and the person would have to find their way back to the app.
 *
 * Only http(s) links are opened; anything else is ignored.
 */

import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

export type OpenedIn = 'in_app' | 'new_tab' | 'same_tab' | 'ignored';

/** One name for every meeting window, so joining again reuses it instead of piling up windows. */
const MEETING_WINDOW_NAME = 'peopleorbit-live-meeting';

/**
 * Open `url` in a small window of its own (web only) and hand the window back, so the page can tell
 * when the person closes it. Call it synchronously from a click handler. Returns `null` in the phone
 * app (it has the in-app browser), for a link that is not http(s), or when the browser blocks the
 * window: the caller then falls back to `openExternalLink`.
 *
 * The window is cut loose from this page (`opener = null`) so the meeting site cannot steer it; the
 * page keeps its own handle, which is enough to read `closed`.
 */
export function openMeetingWindow(url: string): Window | null {
  if (!isHttpUrl(url) || isNativeApp()) return null;
  const width = 1100;
  const height = 760;
  const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
  const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));
  const opened = window.open(
    url,
    MEETING_WINDOW_NAME,
    `popup=yes,width=${width},height=${height},left=${left},top=${top}`,
  );
  if (!opened) return null;
  try {
    opened.opener = null;
  } catch {
    // A cross-origin window may refuse; the window is open either way.
  }
  return opened;
}

/** True inside the Capacitor-wrapped phone app, false in a normal browser. */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Open `url` for the person. Call it synchronously from a click handler. Returns where it opened
 * (synchronously, except that an in-app browser failure falls back to the same tab afterwards).
 */
export function openExternalLink(url: string): OpenedIn {
  if (!isHttpUrl(url)) return 'ignored';
  if (isNativeApp()) {
    Browser.open({ url }).catch(() => {
      // The plugin could not open its window: leave PeopleOrbit rather than do nothing.
      window.location.assign(url);
    });
    return 'in_app';
  }
  const opened = window.open(url, '_blank');
  if (opened) {
    // The new tab must not be able to steer this one.
    try {
      opened.opener = null;
    } catch {
      // A cross-origin window may refuse; the tab is open either way.
    }
    return 'new_tab';
  }
  window.location.assign(url);
  return 'same_tab';
}
